<template>
  <div class="list-page">
    <van-pull-refresh
      v-model="refreshing"
      :success-text="refreshSuccessText"
      @refresh="onRefresh"
      :disabled="table_pullDisable"
      class="flex_vh100"
    >
      <div class="list-data" @scroll="onScroll">
        <van-list
          v-model="loading"
          :finished="finished"
          :finished-text="finishedText"
          :error.sync="error"
          error-text="加载失败，点击重试"
          @load="onLoad"
        >
          <div v-if="$slots.header" class="list-header">
            <slot name="header" />
          </div>

          <slot
            name="default"
            :list="list"
            :loading="loading"
            :finished="finished"
          />

          <div
            v-if="$slots.empty && finished && list.length === 0"
            class="list-empty"
          >
            <slot name="empty">
              <van-empty description="暂无数据" />
            </slot>
          </div>
        </van-list>
      </div>
    </van-pull-refresh>
  </div>
</template>

<script>
import { Toast } from "vant";

export default {
  name: "ListPage",
  props: {
    // API 请求方法，必须返回 Promise
    requestApi: {
      type: Function,
      required: true,
    },
    // 请求参数（会作为参数传给 requestApi）
    params: {
      type: Object,
      default: () => ({}),
    },
    // 每页数量
    pageSize: {
      type: Number,
      default: 50,
    },
    // 是否立即加载
    immediate: {
      type: Boolean,
      default: true,
    },
    // 下拉刷新成功提示文字
    refreshSuccessText: {
      type: String,
      default: "刷新成功",
    },
    // 列表为空时的提示文字
    emptyText: {
      type: String,
      default: "暂无数据",
    },
    // 加载完成提示文字
    finishedText: {
      type: String,
      default: "没有更多了",
    },
  },
  data() {
    return {
      list: [], // 列表数据
      loading: false, // 上拉加载 loading
      refreshing: false, // 下拉刷新 loading
      finished: false, // 是否加载完成
      error: false, // 是否加载出错
      page: 1, // 当前页码
      total: 0, // 总条数
      table_pullDisable: false,
    };
  },
  watch: {
    // 监听参数变化，重新加载
    params: {
      deep: true,
      handler() {
        this.reset();
      },
    },
  },
  mounted() {
    if (this.immediate) {
      this.init();
    }
  },
  methods: {
    // 初始化
    init() {
      this.reset();
    },

    onScroll(e) {
      if (e.target.scrollTop != 0) {
        this.table_pullDisable = true;
      } else {
        this.table_pullDisable = false;
      }
    },

    // 重置列表
    reset() {
      this.list = [];
      this.page = 1;
      this.finished = false;
      this.loading = true;
      this.refreshing = false;
      this.error = false;
      this.onLoad();
    },

    // 下拉刷新
    async onRefresh() {
      this.page = 1;
      this.finished = false;
      this.loading = true;
      this.error = false;

      try {
        const res = await this.requestApi({
          PageIndex: this.page,
          PageSize: this.pageSize,
          ...this.params,
        });

        // 处理不同格式的返回数据
        const { list, total } = this.parseResponse(res);

        this.list = list;
        this.total = total;

        // 判断是否已加载完所有数据
        if (this.list.length >= this.total) {
          this.finished = true;
        } else {
          this.page++;
        }

        this.$emit("refresh-success", this.list);
        this.$emit("update:list", this.list);
      } catch (error) {
        console.error("刷新失败:", error);
        this.$emit("refresh-error", error);
      } finally {
        this.refreshing = false;
        this.loading = false;
      }
    },

    // 上拉加载更多
    async onLoad() {
      // 防止重复请求
      if (this.refreshing) return;

      try {
        const res = await this.requestApi({
          PageIndex: this.page,
          PageSize: this.pageSize,
          ...this.params,
        });

        const { list, total } = this.parseResponse(res);

        this.total = total;

        if (this.page === 1) {
          this.list = list;
        } else {
          this.list = [...this.list, ...list];
        }

        // 判断是否还有下一页
        if (this.list.length >= this.total) {
          this.finished = true;
        } else {
          this.page++;
        }

        this.error = false;
        this.$emit("load-success", this.list);
        this.$emit("update:list", this.list);
      } catch (error) {
        console.error("加载失败:", error);
        this.error = true;
        this.$emit("load-error", error);
      } finally {
        this.loading = false;
      }
    },

    // 解析不同格式的响应数据
    parseResponse(res) {
      // 支持多种常见返回格式
      let list = [];
      let total = 0;

      if (Array.isArray(res)) {
        // 直接返回数组
        list = res;
        total = res.length;
      } else if (res.List && Array.isArray(res.List)) {
        list = res.List;
        total = res.TotalCount || res.List.length;
      } else if (res.records && Array.isArray(res.records)) {
        // { records: [], total: 100 }
        list = res.records;
        total = res.total || res.records.length;
      } else if (res.items && Array.isArray(res.items)) {
        // { items: [], totalCount: 100 }
        list = res.items;
        total = res.totalCount || res.items.length;
      } else if (res.content && Array.isArray(res.content)) {
        // { content: [], totalElements: 100 }
        list = res.content;
        total = res.totalElements || res.content.length;
      }

      return { list, total };
    },

    // 手动刷新（外部调用）
    refresh() {
      this.onRefresh();
    },

    // 手动重新加载（外部调用，保留当前页码）
    reload() {
      this.loading = true;
      this.onLoad();
    },
  },
};
</script>

<style scoped>
.list-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 12px;
}

.list-header {
  margin-bottom: 8px;
}

.list-empty {
  padding: 40px 0;
  text-align: center;
}

.list-data {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}
</style>